import React, { useEffect, useMemo, useState } from "react";
import {
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../../LoadingSpinner";
import {
  useGetAllWorkflowsQuery,
  useGetAllTeamsSelectedWorkflowIdQuery,
} from "../../../../Services/reports/tickets/ReportTickets";
import { toast } from "react-toastify";
import {
  buildColumnsFromRow,
  extractRows,
  MenuProps,
  processRowValues,
  serializeParams,
} from "./ticketSlReportUtils";

interface TicketSlReportShellProps {
  title: string;
  downloadFileName: string;
  errorMessage: string;
  reportData: unknown;
  reportLoading: boolean;
  reportFetching: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
  params: Record<string, string | number> | null;
  setParams: React.Dispatch<
    React.SetStateAction<Record<string, string | number> | null>
  >;
  buildExtraParams: () => Record<string, string | number> | null | undefined;
  validateExtra?: () => string | null;
  extraFilters: React.ReactNode | ((ctx: { selectedWorkflowId: string }) => React.ReactNode);
  extraLoading?: boolean;
  onResetExtra?: () => void;
  onWorkflowChange?: (workflowId: string) => void;
}

const dataGridSx = {
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "lightgray",
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "lightgray",
    "&:last-child": { justifyContent: "flex-end" },
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: "bold",
    overflow: "visible",
    whiteSpace: "normal",
    textOverflow: "initial",
    lineHeight: "normal",
    textAlign: "center",
  },
  "& .MuiDataGrid-columnsContainer": {
    backgroundColor: "lightgray",
  },
  "& .MuiDataGrid-cell": {
    textAlign: "center",
  },
  "& .MuiTablePagination-select, .MuiTablePagination-actions": {
    marginBottom: "10px",
  },
  "& .MuiDataGrid-overlayWrapper": {
    height: "50px",
  },
};

const TicketSlReportShell: React.FC<TicketSlReportShellProps> = ({
  title,
  downloadFileName,
  errorMessage,
  reportData,
  reportLoading,
  reportFetching,
  isError,
  error,
  refetch,
  params,
  setParams,
  buildExtraParams,
  validateExtra,
  extraFilters,
  extraLoading = false,
  onResetExtra,
  onWorkflowChange,
}) => {
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [selectedWorkflowId, setSelectedWorkflowId] = useState("");
  const [dataRows, setDataRows] = useState<any[]>([]);
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });

  const { data: workflowsData, isLoading: workflowsLoading } =
    useGetAllWorkflowsQuery({});
  const { data: teamData, isLoading: teamLoading } =
    useGetAllTeamsSelectedWorkflowIdQuery(selectedWorkflowId, {
      skip: !selectedWorkflowId,
    });

  const theTeams = useMemo(
    () =>
      Array.isArray(teamData?.results)
        ? teamData.results.map((team: any) => ({
            id: team.id,
            name: team.team_name,
          }))
        : [],
    [teamData],
  );
  const theWorkflows = useMemo(
    () => (Array.isArray(workflowsData?.results) ? workflowsData.results : []),
    [workflowsData],
  );

  useEffect(() => {
    if (!params) {
      setDataRows([]);
      setColumns([]);
      return;
    }
    if (isError) {
      toast.error(
        (error as { data?: { message?: string } })?.data?.message ||
          errorMessage,
      );
      setDataRows([]);
      setColumns([]);
      return;
    }
    if (!reportData) return;

    const rows = extractRows(reportData);
    if (rows.length > 0) {
      const processedRows = rows.map((row, index) => ({
        id: index + 1,
        ...processRowValues(row as Record<string, unknown>),
      }));
      setColumns(buildColumnsFromRow(processedRows[0]));
      setDataRows(processedRows);
    } else {
      setColumns([]);
      setDataRows([]);
    }
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [params, reportData, isError, error, errorMessage]);

  const handleWorkflowChange = (workflowId: string) => {
    setSelectedWorkflowId(workflowId);
    setSelectedTeamId("");
    onWorkflowChange?.(workflowId);
    onResetExtra?.();
  };

  const handleSubmit = () => {
    if (!dateFrom) return toast.error('Please select "From Date"');
    if (!dateTo) return toast.error('Please select "To Date"');
    if (!selectedTeamId) return toast.error('Please select "Team"');
    if (!selectedWorkflowId) return toast.error('Please select "Workflow"');

    const extraValidation = validateExtra?.();
    if (extraValidation) return toast.error(extraValidation);

    const extraParams = buildExtraParams();
    if (extraParams === null) return;

    const queryParams: Record<string, string | number> = {
      gte: dateFrom,
      lte: dateTo,
      team_id: selectedTeamId,
      workflow_id: selectedWorkflowId,
      ...(extraParams ?? {}),
    };

    if (params && serializeParams(params) === serializeParams(queryParams)) {
      setParams(queryParams);
      refetch();
      return;
    }

    setParams(queryParams);
  };

  const resetFilters = () => {
    setDateFrom("");
    setDateTo("");
    setParams(null);
    setSelectedWorkflowId("");
    setSelectedTeamId("");
    onWorkflowChange?.("");
    onResetExtra?.();
    setDataRows([]);
    setColumns([]);
    setPaginationModel({ page: 0, pageSize: 20 });
  };

  const handleDownload = () => {
    if (!params || dataRows.length === 0 || columns.length === 0) {
      toast.error("Please apply filters and submit before downloading.");
      return;
    }
    const headers = columns.map((col) => col.headerName ?? col.field);
    const fields = columns.map((col) => String(col.field));
    const csvContent = [
      headers.map((h) => `"${h}"`).join(","),
      ...dataRows.map((row, index) =>
        fields
          .map((field) => {
            const value = field === "sno" ? index + 1 : row[field];
            return `"${String(value ?? "").replace(/"/g, '""')}"`;
          })
          .join(","),
      ),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${downloadFileName}.csv`;
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
    link.remove();
    toast.success("Download started.");
  };

  const paginatedRows = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    return dataRows
      .slice(start, start + paginationModel.pageSize)
      .map((row, index) => ({
        ...row,
        sno: start + index + 1,
      }));
  }, [dataRows, paginationModel.page, paginationModel.pageSize]);

  const isLoading =
    workflowsLoading ||
    teamLoading ||
    extraLoading ||
    reportLoading ||
    reportFetching;

  return (
    <div id="kt_app_content_container" className="app-container mx-5">
      <div className="card card-flush">
        <div
          className="card-header align-items-center py-1 gap-2 gap-md-5"
          style={{ justifyContent: "flex-start" }}
        >
          <button
            className="btn btn-link mb-2"
            onClick={() => navigate("/reports/ticket/reports-list")}
            style={{ marginRight: "-17px" }}
          >
            <i
              className="bi bi-arrow-90deg-left"
              style={{ fontSize: "20px", color: "black", cursor: "pointer" }}
            />
          </button>
          <span className="fs-3 text-gray-800 fw-bold ms-3">{title}</span>
        </div>

        <div
          className="card-toolbar flex-row-fluid justify-content-start gap-5 d-flex mb-10 flex-wrap"
          style={{ marginLeft: "30px" }}
        >
          <div className="w-100 mw-150px">
            <TextField
              id="date-from"
              label="From"
              type="date"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                inputProps: {
                  lang: "en-GB",
                  style: { textTransform: "uppercase" },
                  max: new Date().toISOString().split("T")[0],
                },
              }}
              fullWidth
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div className="w-100 mw-150px">
            <TextField
              id="date-to"
              label="To"
              type="date"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                inputProps: {
                  lang: "en-GB",
                  style: { textTransform: "uppercase" },
                  max: new Date().toISOString().split("T")[0],
                },
              }}
              fullWidth
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
          <div className="w-100 mw-200px">
            <FormControl fullWidth>
              <InputLabel id="workflow-select-label">Workflow</InputLabel>
              <Select
                labelId="workflow-select-label"
                value={selectedWorkflowId}
                label="Workflow"
                onChange={(e) => handleWorkflowChange(String(e.target.value))}
                MenuProps={MenuProps}
              >
                <MenuItem value="">
                  <em>Select workflow</em>
                </MenuItem>
                {theWorkflows.map((workflow: any) => (
                  <MenuItem key={workflow.id} value={workflow.id}>
                    {workflow.workflow_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="w-100 mw-200px">
            <FormControl fullWidth>
              <InputLabel id="team-select-label">Select teams</InputLabel>
              <Select
                labelId="team-select-label"
                value={selectedTeamId}
                label="Select teams"
                disabled={!selectedWorkflowId}
                onChange={(e) => setSelectedTeamId(e.target.value)}
                MenuProps={MenuProps}
              >
                <MenuItem value="">
                  <em>Select team</em>
                </MenuItem>
                {theTeams.map((team: any) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {typeof extraFilters === "function"
            ? extraFilters({ selectedWorkflowId })
            : extraFilters}

          <div className="ms-auto d-flex gap-3" style={{ marginRight: "20px" }}>
            <button className="btn btn-primary" onClick={handleSubmit}>
              Submit
            </button>
            <button
              className="btn btn-primary"
              onClick={handleDownload}
              disabled={!params || dataRows.length === 0}
            >
              Download
            </button>
            <button className="btn btn-link" onClick={resetFilters}>
              <i className="bi bi-funnel fs-2 text-dark" />
            </button>
          </div>
        </div>

        <CardContent style={{ width: "100%" }}>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <DataGrid
              rows={paginatedRows}
              columns={columns}
              pagination
              paginationMode="server"
              rowCount={dataRows.length}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 15, 20]}
              disableColumnMenu
              localeText={{
                noRowsLabel: "No rows found",
              }}
              sx={dataGridSx}
            />
          )}
        </CardContent>
      </div>
    </div>
  );
};

export default TicketSlReportShell;
